import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import InfoIcon from '@mui/icons-material/Info';
import { format } from 'date-fns';
import { getPurchaseReceipts, getPurchaseReceiptById, PurchaseReceipt, PurchaseReceiptItem } from '../firebase/inventoryCostServices';
import { formatCurrency } from '../utils/formatters';
import { useSnackbar } from 'notistack';
import { generatePurchaseReceiptGLEntryPreviews, createPurchaseReceiptGLTransaction, GLEntryPreview, reversePurchaseReceiptGLTransaction } from '../services/glPurchaseReceiptService';
import GLPurchaseReceiptEntryPreviewDialog from '../components/GLPurchaseReceiptEntryPreviewDialog';
import ReversePurchaseReceiptGLDialog from '../components/ReversePurchaseReceiptGLDialog';

const PurchaseReceiptHistory: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [receipts, setReceipts] = useState<PurchaseReceipt[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedReceipt, setSelectedReceipt] = useState<PurchaseReceipt | null>(null);
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
  
  // GL Entry preview state
  const [glEntryPreviewOpen, setGlEntryPreviewOpen] = useState<boolean>(false);
  const [glEntryPreviews, setGlEntryPreviews] = useState<GLEntryPreview[]>([]);
  const [isSubmittingGLEntries, setIsSubmittingGLEntries] = useState<boolean>(false);
  
  // GL Transaction reversal state
  const [reverseDialogOpen, setReverseDialogOpen] = useState<boolean>(false);
  const [receiptToReverse, setReceiptToReverse] = useState<PurchaseReceipt | null>(null);
  
  const { enqueueSnackbar } = useSnackbar();

  // Fetch purchase receipts
  useEffect(() => {
    const fetchReceipts = async () => {
      setLoading(true);
      try {
        const data = await getPurchaseReceipts(100); // Limit to 100 receipts initially
        setReceipts(data);
      } catch (error) {
        console.error('Error fetching purchase receipts:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchReceipts();
  }, []);

  // Handle search
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  // Filter receipts based on search query and date range
  const filteredReceipts = receipts.filter(receipt => {
    // Apply search filter
    const matchesSearch = 
      receipt.receiptNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      receipt.vendorName.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Apply date filters if set
    let matchesDateRange = true;
    const receiptDate = receipt.receiptDate instanceof Date 
      ? receipt.receiptDate 
      : new Date((receipt.receiptDate as any).seconds * 1000);
    
    if (startDate) {
      matchesDateRange = matchesDateRange && receiptDate >= startDate;
    }
    
    if (endDate) {
      // Add one day to include the end date fully
      const endDatePlusOne = new Date(endDate);
      endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
      matchesDateRange = matchesDateRange && receiptDate < endDatePlusOne;
    }
    
    return matchesSearch && matchesDateRange;
  });

  // View receipt details
  const handleViewDetails = async (receiptId: string) => {
    try {
      const receipt = await getPurchaseReceiptById(receiptId);
      if (receipt) {
        setSelectedReceipt(receipt);
        setDetailsOpen(true);
      }
    } catch (error) {
      console.error('Error fetching receipt details:', error);
    }
  };

  // Close details dialog
  const handleCloseDetails = () => {
    setDetailsOpen(false);
  };

  // Clear filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setStartDate(null);
    setEndDate(null);
  };
  
  // Handle opening GL entry preview dialog
  const handleOpenGLEntryPreview = (receipt: PurchaseReceipt) => {
    try {
      // Generate GL entry previews
      const entries = generatePurchaseReceiptGLEntryPreviews(receipt);
      setGlEntryPreviews(entries);
      setSelectedReceipt(receipt);
      setGlEntryPreviewOpen(true);
    } catch (error) {
      console.error('Error generating GL entry previews:', error);
      enqueueSnackbar('Failed to generate GL entries', { variant: 'error' });
    }
  };
  
  // Handle closing GL entry preview dialog
  const handleCloseGLEntryPreview = () => {
    setGlEntryPreviewOpen(false);
  };
  
  // Handle confirming and posting GL entries
  const handleConfirmGLEntries = async () => {
    if (!selectedReceipt) return;
    
    setIsSubmittingGLEntries(true);
    
    try {
      // Create GL transaction
      const transactionId = await createPurchaseReceiptGLTransaction(selectedReceipt, glEntryPreviews);
      
      // Close dialog and show success message
      setGlEntryPreviewOpen(false);
      enqueueSnackbar('GL entries posted successfully', { variant: 'success' });
      
      // Refresh the receipts list
      const updatedReceipts = await getPurchaseReceipts(100);
      setReceipts(updatedReceipts);
    } catch (error) {
      console.error('Error posting GL entries:', error);
      enqueueSnackbar(`Failed to post GL entries: ${error instanceof Error ? error.message : 'Unknown error'}`, { variant: 'error' });
    } finally {
      setIsSubmittingGLEntries(false);
    }
  };
  
  // Handle opening reverse GL transaction dialog
  const handleOpenReverseDialog = (receipt: PurchaseReceipt) => {
    setReceiptToReverse(receipt);
    setReverseDialogOpen(true);
  };
  
  // Handle closing reverse GL transaction dialog
  const handleCloseReverseDialog = () => {
    setReverseDialogOpen(false);
  };
  
  // Handle confirming reverse GL transaction
  const handleConfirmReverseGLTransaction = async (): Promise<void> => {
    if (!receiptToReverse || !receiptToReverse.id) return;
    
    try {
      // Reverse the GL transaction
      await reversePurchaseReceiptGLTransaction(receiptToReverse.id);
      
      // Show success message
      enqueueSnackbar('GL transaction reversed successfully', { variant: 'success' });
      
      // Refresh the receipts list
      const updatedReceipts = await getPurchaseReceipts(100);
      setReceipts(updatedReceipts);
    } catch (error) {
      console.error('Error reversing GL transaction:', error);
      enqueueSnackbar(`Failed to reverse GL transaction: ${error instanceof Error ? error.message : 'Unknown error'}`, { variant: 'error' });
      throw error; // Re-throw to propagate to the dialog
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h1" fontWeight={600}>
          Purchase Receipt History
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and search purchase receipts
        </Typography>
      </Box>
      
      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search by receipt # or vendor..."
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, minWidth: '250px' }}
          />
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{ textField: { size: 'small' } }}
            />
            
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{ textField: { size: 'small' } }}
            />
          </LocalizationProvider>
          
          <Button 
            variant="outlined" 
            size="small" 
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>
      
      {/* Receipts Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Receipt #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell align="right">Total Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : filteredReceipts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    No purchase receipts found
                  </TableCell>
                </TableRow>
              ) : (
                filteredReceipts.map((receipt) => (
                  <TableRow key={receipt.id}>
                    <TableCell>{receipt.receiptNumber}</TableCell>
                    <TableCell>
                      {format(
                      receipt.receiptDate instanceof Date
                        ? receipt.receiptDate
                        : new Date((receipt.receiptDate as any).seconds * 1000), 
                      'dd/MM/yyyy'
                    )}
                    </TableCell>
                    <TableCell>{receipt.vendorName}</TableCell>
                    <TableCell align="right">{formatCurrency(receipt.totalAmount)}</TableCell>
                    <TableCell>
                      {receipt.glTransactionId ? (
                        <Chip
                          label="GL Posted"
                          size="small"
                          color="success"
                          icon={<CheckCircleIcon fontSize="small" />}
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          label="GL Not Posted"
                          size="small"
                          color="default"
                          icon={<InfoIcon fontSize="small" />}
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => handleViewDetails(receipt.id as string)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {receipt.glTransactionId ? (
                          <Tooltip title="Reverse GL Transaction">
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleOpenReverseDialog(receipt)}
                            >
                              <UndoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Post GL Entries">
                            <IconButton 
                              size="small" 
                              color="secondary" 
                              onClick={() => handleOpenGLEntryPreview(receipt)}
                            >
                              <AccountBalanceIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Receipt Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        {selectedReceipt && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ReceiptIcon sx={{ mr: 1 }} />
                Purchase Receipt #{selectedReceipt.receiptNumber}
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2">Vendor</Typography>
                  <Typography variant="body1">{selectedReceipt.vendorName}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2">Date</Typography>
                  <Typography variant="body1">
                    {format(
                      selectedReceipt.receiptDate instanceof Date
                        ? selectedReceipt.receiptDate
                        : new Date((selectedReceipt.receiptDate as any).seconds * 1000),
                      'dd/MM/yyyy'
                    )}
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2">Payment Status</Typography>
                  <Chip
                    label={selectedReceipt.paymentStatus}
                    size="small"
                    color={
                      selectedReceipt.paymentStatus === 'paid'
                        ? 'success'
                        : selectedReceipt.paymentStatus === 'partial'
                        ? 'warning'
                        : 'error'
                    }
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2">Total Amount</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatCurrency(selectedReceipt.totalAmount)}
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom>
                Items
              </Typography>
              
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SKU</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Unit Cost</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedReceipt.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.productSku}</TableCell>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unitCost)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.totalCost)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {selectedReceipt.notes && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2">Notes</Typography>
                  <Typography variant="body2">{selectedReceipt.notes}</Typography>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetails}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* GL Entry Preview Dialog */}
      {selectedReceipt && (
        <GLPurchaseReceiptEntryPreviewDialog
          open={glEntryPreviewOpen}
          onClose={handleCloseGLEntryPreview}
          onConfirm={handleConfirmGLEntries}
          entries={glEntryPreviews}
          isSubmitting={isSubmittingGLEntries}
          receiptNumber={selectedReceipt.receiptNumber}
          vendorName={selectedReceipt.vendorName}
          totalAmount={selectedReceipt.totalAmount}
        />
      )}
      
      {/* Reverse GL Transaction Dialog */}
      {receiptToReverse && (
        <ReversePurchaseReceiptGLDialog
          open={reverseDialogOpen}
          onClose={handleCloseReverseDialog}
          onConfirm={handleConfirmReverseGLTransaction}
          receiptNumber={receiptToReverse.receiptNumber}
          vendorName={receiptToReverse.vendorName}
        />
      )}
    </Box>
  );
};

export default PurchaseReceiptHistory;
